import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { AIStatus } from '@vendasta/conversation';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  WebchatButtonComponent,
  WebchatMultiLocationBoxComponent,
  DefaultPosition,
  TEMP_CONVERSATION_ID,
} from '@galaxy/conversation/webchat-widget';
import { SentMessage } from '@vendasta/galaxy/chat-composer/src/chat-composer.component';
import { escapeHtml } from '@vendasta/galaxy/pipes/src/escape-html/escape-html.pipe';
import { AuthService } from '../../services/auth.service';
import { ChatService } from '../../services/chat.service';
import { getCookie, setCookie, initCookieProxy, VIEW_COOKIE_NAME } from '../../util/cookie';
import { WebchatConfigService } from '../../services/webchat-config.service';
import { LocationRow, WebchatLocationService } from '../../services/webchat-location.service';

const BUBBLE_SIZE = 100;
const MAX_WIDTH = 432;
const MAX_HEIGHT = 600;

@Component({
  selector: 'app-webchat-multi-location-root',
  imports: [WebchatMultiLocationBoxComponent, WebchatButtonComponent],
  templateUrl: './webchat-multi-location-root.component.html',
  styleUrls: ['./webchat-multi-location-root.component.scss'],
  providers: [WebchatLocationService],
  encapsulation: ViewEncapsulation.Emulated,
})
export class WebchatMultiLocationRootComponent implements OnInit {
  @Input() widgetId = '';
  @Input() embedded = false;

  private readonly configSvc = inject(WebchatConfigService);
  private readonly locationService = inject(WebchatLocationService);
  private readonly authService = inject(AuthService);
  private readonly chatService = inject(ChatService);
  private readonly el = inject(ElementRef);
  private parentOriginValue = '*';

  protected readonly isOpen = signal(false);
  protected readonly view = signal<'picker' | 'chat'>('picker');
  protected readonly selectedLocation = signal<LocationRow | null>(null);
  protected readonly currentPage = signal(1);
  protected readonly isMobile = signal(false);

  protected readonly config = this.configSvc.multiLocationConfig;
  protected readonly isEnabled = computed(() => this.config()?.isEnabled);
  protected readonly position = signal<'right' | 'left'>(DefaultPosition);
  protected readonly configError = signal(false);
  private readonly greetingAdded = signal(false);

  protected readonly locations = toSignal(this.locationService.locations$, { initialValue: [] as LocationRow[] });
  protected readonly isLoading = toSignal(this.locationService.isLoading$, { initialValue: true });
  protected readonly hasMore = toSignal(this.locationService.hasMore$, { initialValue: false });
  protected readonly hasPrev = toSignal(this.locationService.hasPrev$, { initialValue: false });
  protected readonly error = toSignal(this.locationService.error$, { initialValue: false });

  protected readonly currentParticipantId = this.authService.currentParticipantId;
  protected readonly messageGroups = toSignal(this.chatService.messageGroups$, { initialValue: [] });
  protected readonly messages = toSignal(this.chatService.messages$);
  protected readonly isCreatingConversation = computed(() => this.chatService.isCreatingConversation());
  protected readonly isReplying = computed(() => {
    const messages = this.messages();
    if (!messages?.length) return false;
    const latest = messages[0];
    if (latest?.aiStatus === AIStatus.AI_RECEIVED || latest?.aiStatus === AIStatus.AI_THINKING) return true;
    return latest?.sender?.participantId === this.currentParticipantId();
  });

  constructor() {
    const el = this.el.nativeElement;
    this.parentOriginValue = el.getAttribute('data-parent-origin') || window.location.origin;
    this.isMobile.set(el.getAttribute('data-is-mobile') === 'true');
    initCookieProxy(el.getAttribute('data-cookies') || '', this.parentOriginValue);

    const urlEncoded = el.getAttribute('parent-url');
    if (urlEncoded) this.configSvc.hostPageUrl.set(decodeURIComponent(urlEncoded));
    const hostname = el.getAttribute('parent-hostname');
    if (hostname) this.configSvc.hostPageHostname.set(hostname);

    effect(() => {
      if (!this.config() || this.embedded) return;
      const open = this.isOpen();
      window.parent.postMessage(
        {
          type: 'webchat:resize',
          detail: {
            widgetId: this.widgetId,
            width: open ? MAX_WIDTH : BUBBLE_SIZE,
            height: open ? MAX_HEIGHT : BUBBLE_SIZE,
            position: this.position(),
            widgetTopSpace: 0,
          },
        },
        this.parentOriginValue,
      );
    });
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent): void {
    if (event.source !== window.parent) return;
    if (event.data?.type === 'webchat:mobile-change') {
      this.isMobile.set(!!event.data.detail?.isMobile);
      return;
    }
    if (event.data?.type === 'webchat:url-change') {
      const url = event.data.detail?.url;
      if (typeof url === 'string' && url.length > 0) {
        this.configSvc.hostPageUrl.set(url);
      }
    }
  }

  ngOnInit(): void {
    this.setupFocusNotification();
    this.setupLinkInterceptor();
    this.configSvc
      .loadMultiLocationWidget(this.widgetId)
      .then(async () => {
        const cfg = this.config();
        if (cfg) {
          this.position.set(cfg.position ?? DefaultPosition);
          this.locationService.init(this.widgetId);
          await this.restoreSelectedLocation();
          this.isOpen.set(this.getInitialOpenState());
          window.parent.postMessage(
            { type: 'webchat:ready', detail: { widgetId: this.widgetId } },
            this.parentOriginValue,
          );
        }
      })
      .catch(() => {
        this.configError.set(true);
      });
  }

  protected async openWidget(): Promise<void> {
    const loc = this.selectedLocation();
    if (loc) {
      this.authService.loadSession(this.widgetId);
      if (!this.chatService.conversation()) {
        await this.chatService.openConversation();
        this.maybeAddGreeting();
      }
      this.view.set('chat');
    } else {
      this.view.set('picker');
    }
    this.isOpen.set(true);
    setCookie(VIEW_COOKIE_NAME, 'true');
  }

  protected closeWidget(): void {
    this.isOpen.set(false);
    setCookie(VIEW_COOKIE_NAME, 'false');
    this.currentPage.set(1);
    this.locationService.setSearchTerm('');
  }

  protected retrySearch(): void {
    this.currentPage.set(1);
    this.locationService.setSearchTerm('');
  }

  protected onSearch(term: string): void {
    this.currentPage.set(1);
    this.locationService.setSearchTerm(term);
  }

  protected async selectLocation(loc: LocationRow): Promise<void> {
    this.authService.clearToken(this.widgetId);
    this.configSvc.selectedNamespace.set(loc.namespace);
    this.authService.loadSession(this.widgetId);
    this.selectedLocation.set(loc);
    sessionStorage.setItem(this.locationStorageKey(), JSON.stringify(loc));
    this.view.set('chat');
    await this.chatService.openConversation();
    this.maybeAddGreeting();
  }

  protected async sendMessage(msg: SentMessage): Promise<void> {
    if (!msg?.messageText) return;
    await this.chatService.sendMessage(escapeHtml(msg.messageText));
  }

  protected goBack(): void {
    this.authService.clearToken(this.widgetId);
    this.configSvc.selectedNamespace.set(null);
    this.chatService.clearConversation();
    this.greetingAdded.set(false);
    sessionStorage.removeItem(this.locationStorageKey());
    this.view.set('picker');
    this.selectedLocation.set(null);
  }

  protected nextPage(): void {
    this.locationService.nextPage();
    this.currentPage.update((p) => p + 1);
  }

  protected prevPage(): void {
    this.locationService.prevPage();
    this.currentPage.update((p) => p - 1);
  }

  private maybeAddGreeting(): void {
    if (this.greetingAdded()) return;
    if (!this.chatService.conversation() && this.configSvc.activeConfig()?.enableGreetingMessage) {
      this.chatService.addGreetingMessage(TEMP_CONVERSATION_ID);
      this.greetingAdded.set(true);
    }
  }

  private locationStorageKey(): string {
    return `webchat-ml-loc-${this.widgetId}`;
  }

  private getInitialOpenState(): boolean {
    if (getCookie(VIEW_COOKIE_NAME) !== 'true') return false;
    return !this.isMobile();
  }

  private async restoreSelectedLocation(): Promise<void> {
    const raw = sessionStorage.getItem(this.locationStorageKey());
    if (!raw) return;
    try {
      const loc = JSON.parse(raw) as LocationRow;
      if (!loc?.namespace || !loc?.businessName) {
        sessionStorage.removeItem(this.locationStorageKey());
        return;
      }
      this.configSvc.selectedNamespace.set(loc.namespace);
      this.authService.loadSession(this.widgetId);
      this.selectedLocation.set(loc);
      this.view.set('chat');
      await this.chatService.openConversation();
      this.maybeAddGreeting();
    } catch {
      sessionStorage.removeItem(this.locationStorageKey());
    }
  }

  private setupFocusNotification(): void {
    if (this.embedded) return;

    const notify = (focused: boolean) => {
      window.parent.postMessage({ type: 'webchat:input-focus', detail: { focused } }, this.parentOriginValue);
    };

    const isTextInput = (el: EventTarget | null): boolean =>
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLInputElement ||
      (el instanceof HTMLElement && el.isContentEditable);

    this.el.nativeElement.addEventListener('focusin', (e: FocusEvent) => {
      if (isTextInput(e.target)) notify(true);
    });
    this.el.nativeElement.addEventListener('focusout', (e: FocusEvent) => {
      if (isTextInput(e.target)) notify(false);
    });
  }

  private setupLinkInterceptor(): void {
    this.el.nativeElement.addEventListener('click', (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest('a');
      if (!anchor?.href) return;

      event.preventDefault();
      if (anchor.target === '_blank') {
        window.open(anchor.href, '_blank', 'noopener,noreferrer');
      } else {
        window.parent.location.href = anchor.href;
      }
    });
  }
}
